import { isNative } from '~/plugins/native/capacitor'

// Initialise over the air updates from our self hosted OTA system
export default async function ({ store, app, $log, redirect, route }, inject) {
  const otaUpdates = {}

  if (isNative) {
    const { CapacitorUpdater } = await import('@capgo/capacitor-updater')
    const { SplashScreen } = await import('@capacitor/splash-screen')

    let data = null

    $log.debug('OTA notifying ready')

    CapacitorUpdater.notifyAppReady()

    // Capgo names every downloaded bundle folder with a 10-character alphanumeric id
    // (e.g. "5NXssH4iu9"). Matching on this shape lets us tell bundle folders apart from
    // iOS system folders (Caches, WebKit, Cookies, Preferences, …) so cleanup can never
    // touch anything that isn't ours.
    const bundleIdPattern = /^[A-Za-z0-9]{10}$/

    // Remove stale OTA bundles so an install's "Documents & Data" doesn't balloon over
    // time. We keep the running bundle, the built-in bundle, any bundle queued to load on
    // next launch (status "pending"), and the most recent previous successful bundle so a
    // rollback is still possible. Everything else is deleted.
    otaUpdates.cleanupOldBundles = async () => {
      try {
        const current = await CapacitorUpdater.current()
        const currentId = current?.bundle?.id

        $log.debug('OTA cleanup: current bundle', {
          id: currentId,
          version: current?.bundle?.version,
          status: current?.bundle?.status,
          native: current?.native
        })

        if (!currentId) {
          $log.debug('OTA cleanup: no current bundle id, skipping')

          return
        }

        const { bundles } = await CapacitorUpdater.list()

        $log.debug('OTA cleanup: bundles before', {
          count: bundles?.length ?? 0,
          bundles: (bundles ?? []).map(bundle => ({
            id: bundle.id,
            version: bundle.version,
            status: bundle.status,
            downloaded: bundle.downloaded
          }))
        })

        if (!bundles?.length) {
          return
        }

        const keepIds = new Set([currentId, 'builtin'])

        // Keep only the newest pending bundle — the one queued to load next / just
        // downloaded this launch. Older pending bundles are stale downloads that were
        // superseded before they were ever activated and only waste space (their extracted
        // copies sit in Library/NoCloud), so let Capgo delete them.
        const [newestPending] = bundles
          .filter(bundle => bundle.status === 'pending')
          .sort((a, b) => new Date(b.downloaded) - new Date(a.downloaded))

        if (newestPending) {
          keepIds.add(newestPending.id)
        }

        // Keep the most recently downloaded previous successful bundle for rollback.
        const [previousSuccessfulBundle] = bundles
          .filter(bundle => bundle.status === 'success' && bundle.id !== currentId && bundle.id !== 'builtin')
          .sort((a, b) => new Date(b.downloaded) - new Date(a.downloaded))

        if (previousSuccessfulBundle) {
          keepIds.add(previousSuccessfulBundle.id)
        }

        $log.debug('OTA cleanup: keeping bundles', [...keepIds])

        const deleted = []
        const failed = []

        for (const bundle of bundles) {
          if (keepIds.has(bundle.id)) {
            continue
          }

          try {
            await CapacitorUpdater.delete({ id: bundle.id })

            deleted.push({ id: bundle.id, version: bundle.version })

            $log.debug('OTA cleanup: deleted bundle', { id: bundle.id, version: bundle.version })
          } catch (error) {
            failed.push({ id: bundle.id, version: bundle.version, error: error?.message })

            $log.debug('OTA cleanup: failed to delete bundle', {
              id: bundle.id,
              version: bundle.version,
              error: error?.message
            })
          }
        }

        // Re-list after deletion so the saved logs show whether anything was actually reclaimed.
        const after = await CapacitorUpdater.list()

        $log.debug('OTA cleanup: summary', {
          deletedCount: deleted.length,
          failedCount: failed.length,
          remainingCount: after?.bundles?.length ?? 0,
          remaining: (after?.bundles ?? []).map(bundle => ({
            id: bundle.id,
            version: bundle.version,
            status: bundle.status
          }))
        })
      } catch (error) {
        $log.debug('OTA cleanup: error', { error: error?.message })
      }
    }

    // Walk the app's sandbox directories and report how much disk each one uses. iOS
    // "Documents & Data" covers far more than OTA bundles (WebView storage, caches,
    // persisted state), so this breakdown tells us which folder is actually the culprit.
    otaUpdates.reportStorageUsage = async () => {
      try {
        const { Filesystem, Directory } = await import('@capacitor/filesystem')

        const formatBytes = bytes => {
          if (!bytes) {
            return '0 B'
          }

          const units = ['B', 'KB', 'MB', 'GB']
          const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)

          return `${(bytes / Math.pow(1024, exponent)).toFixed(1)} ${units[exponent]}`
        }

        // Recursively sum every file under a directory. Depth is capped so a deep tree
        // can't spin forever, and unreadable paths are skipped rather than aborting.
        const measure = async (directory, path, depth = 0) => {
          let total = 0
          let entries = []

          try {
            const result = await Filesystem.readdir({ directory, path })

            entries = result.files ?? []
          } catch (error) {
            return total
          }

          for (const entry of entries) {
            const entryPath = `${path}/${entry.name}`

            if (entry.type === 'directory') {
              if (depth < 6) {
                total += await measure(directory, entryPath, depth + 1)
              }
            } else {
              total += entry.size ?? 0
            }
          }

          return total
        }

        const roots = [
          { label: 'Data', directory: Directory.Data },
          { label: 'Documents', directory: Directory.Documents },
          { label: 'Library', directory: Directory.Library },
          { label: 'Cache', directory: Directory.Cache }
        ]

        const report = {}

        for (const root of roots) {
          try {
            const { files } = await Filesystem.readdir({ directory: root.directory, path: '.' })

            const children = {}
            let total = 0

            for (const entry of files ?? []) {
              const size =
                entry.type === 'directory' ? await measure(root.directory, entry.name, 1) : entry.size ?? 0

              children[entry.name] = formatBytes(size)
              total += size
            }

            report[root.label] = { total: formatBytes(total), children }
          } catch (error) {
            report[root.label] = { error: error?.message }
          }
        }

        $log.debug('OTA storage: filesystem usage', report)

        // Cross-reference the bundle folders on disk against the bundles Capgo still
        // tracks. Folders that exist on disk but aren't reported by list()/current() are
        // orphans that our cleanup can never delete via CapacitorUpdater.delete() — the
        // likely cause of unbounded growth (e.g. left behind when resetWhenUpdate wipes
        // the registry on a native binary update). Bundles are stored across Documents,
        // Library root and Library/NoCloud depending on the plugin version.
        try {
          const current = await CapacitorUpdater.current()
          const { bundles } = await CapacitorUpdater.list()

          const currentId = current?.bundle?.id
          const currentVersion = current?.bundle?.version

          const trackedNames = new Set()

          for (const bundle of bundles ?? []) {
            trackedNames.add(bundle.id)
            trackedNames.add(bundle.version)
          }

          const classify = name => {
            if (name === currentId || name === currentVersion) {
              return 'current'
            }

            if (name === 'builtin' || trackedNames.has(name)) {
              return 'tracked'
            }

            return 'orphan'
          }

          const scanLocations = [
            { label: 'Data', directory: Directory.Data, path: '.' },
            { label: 'Library', directory: Directory.Library, path: '.' },
            { label: 'Library/NoCloud', directory: Directory.Library, path: 'NoCloud' }
          ]

          const folders = {}
          let orphanBytes = 0

          for (const location of scanLocations) {
            let entries = []

            try {
              const result = await Filesystem.readdir({ directory: location.directory, path: location.path })

              entries = result.files ?? []
            } catch (error) {
              continue
            }

            for (const entry of entries) {
              // Capgo stores each bundle as an id-named entry — a zip file in Directory.Data
              // or an extracted folder elsewhere — so match on the id shape rather than the
              // type. This also keeps iOS system folders (Caches, WebKit, …) out of scope.
              if (!bundleIdPattern.test(entry.name)) {
                continue
              }

              const entryPath = location.path === '.' ? entry.name : `${location.path}/${entry.name}`
              const size =
                entry.type === 'directory' ? await measure(location.directory, entryPath, 1) : entry.size ?? 0
              const classification = classify(entry.name)

              if (classification === 'orphan') {
                orphanBytes += size
              }

              folders[`${location.label}/${entry.name}`] = { size: formatBytes(size), classification }
            }
          }

          $log.debug('OTA storage: bundle folder reconciliation', {
            currentId,
            currentVersion,
            trackedCount: (bundles ?? []).length,
            tracked: (bundles ?? []).map(bundle => ({ id: bundle.id, version: bundle.version })),
            orphanTotal: formatBytes(orphanBytes),
            folders
          })
        } catch (error) {
          $log.debug('OTA storage: reconciliation error', { error: error?.message })
        }
      } catch (error) {
        $log.debug('OTA storage: report error', { error: error?.message })
      }
    }

    // Delete Capgo bundle folders that were orphaned on disk — present in the sandbox but
    // no longer tracked by list()/current(). These pile up because resetWhenUpdate wipes
    // Capgo's registry on each native binary update while leaving the old folders behind,
    // and CapacitorUpdater.delete() can only remove bundles it still tracks. We only touch
    // loose folders in Documents/Data and Library root whose name matches Capgo's bundle-id
    // shape, and never the current, tracked or builtin bundle — so iOS system folders and
    // Capgo's active NoCloud store are never at risk.
    otaUpdates.purgeOrphanBundles = async () => {
      try {
        const { Filesystem, Directory } = await import('@capacitor/filesystem')

        const current = await CapacitorUpdater.current()
        const currentId = current?.bundle?.id

        // Without a trustworthy view of the running bundle we can't safely decide what is
        // an orphan, so bail rather than risk deleting something in use.
        if (!currentId || currentId === 'builtin') {
          $log.debug('OTA purge: skipped, no active bundle reported', { currentId })

          return
        }

        const { bundles } = await CapacitorUpdater.list()

        const protectedNames = new Set([currentId, current?.bundle?.version, 'builtin'])

        for (const bundle of bundles ?? []) {
          protectedNames.add(bundle.id)
          protectedNames.add(bundle.version)
        }

        const locations = [
          { label: 'Data', directory: Directory.Data },
          { label: 'Library', directory: Directory.Library }
        ]

        const deleted = []
        const failed = []

        for (const location of locations) {
          let entries = []

          try {
            const result = await Filesystem.readdir({ directory: location.directory, path: '.' })

            entries = result.files ?? []
          } catch (error) {
            continue
          }

          for (const entry of entries) {
            // Bundles are id-named zip files in Directory.Data (or extracted folders
            // elsewhere), so identify them by the id shape, not the entry type.
            if (!bundleIdPattern.test(entry.name) || protectedNames.has(entry.name)) {
              continue
            }

            try {
              if (entry.type === 'directory') {
                await Filesystem.rmdir({ directory: location.directory, path: entry.name, recursive: true })
              } else {
                await Filesystem.deleteFile({ directory: location.directory, path: entry.name })
              }

              deleted.push(`${location.label}/${entry.name}`)
            } catch (error) {
              failed.push({ path: `${location.label}/${entry.name}`, error: error?.message })
            }
          }
        }

        $log.debug('OTA purge: orphan bundles removed', {
          deletedCount: deleted.length,
          deleted,
          failedCount: failed.length,
          failed
        })
      } catch (error) {
        $log.debug('OTA purge: error', { error: error?.message })
      }
    }

    otaUpdates.sync = async isActive => {
      if (process.env.APP_ENVIRONMENT === 'local') {
        return false
      }

      const currentBundle = await CapacitorUpdater.current()

      if (currentBundle.bundle?.version) {
        store.commit('device/setBundleVersion', currentBundle.bundle.version)
      }

      const isOnCheckoutPage = app.router.history?.current?.path?.includes('checkout')
      const isOnAuthPage = app.router.history?.current?.path?.includes('auth/')

      if (isActive) {
        store.commit('overTheAirUpdates/setStatusCode', 5)

        try {
          const latest = await CapacitorUpdater.getLatest()

          // $log.debug('OTA latest', latest)

          if (latest.url) {
            store.commit('overTheAirUpdates/setStatusCode', 7)

            $log.debug('OTA downloading...')

            data = await CapacitorUpdater.download({
              url: latest.url,
              version: latest.version
            })

            $log.debug('OTA finished downloading')

            store.commit('overTheAirUpdates/setStatusCode', 6)
          } else {
            store.commit('overTheAirUpdates/setStatusCode', 0)
          }
        } catch (error) {
          console.log('OTA threw error', error)

          store.commit('overTheAirUpdates/setStatusCode', 0)
        }
      } else if (!isActive && data && !isOnCheckoutPage && !isOnAuthPage) {
        $log.debug('OTA: Setting new version')
        // Do the switch when user leave app or when you want
        SplashScreen.show()

        try {
          store.commit('overTheAirUpdates/setStatusCode', 8)

          await CapacitorUpdater.set({ id: data.id })

          store.commit('overTheAirUpdates/setStatusCode', 1)

          redirect('/platform')
        } catch (error) {
          $log.debug('OTA error', error)

          store.commit('overTheAirUpdates/setStatusCode', 3)

          store.commit('overTheAirUpdates/setError', error)

          SplashScreen.hide()
        }
      }
    }

    otaUpdates.setUserId = async userId => {
      if (!userId) {
        return false
      }

      await CapacitorUpdater.setCustomId({ customId: userId })
    }

    // Run sync first, then prune bundles once it settles so cleanup never races a
    // download in progress. Catching sync errors avoids unhandled-rejection warnings.
    otaUpdates
      .sync(true)
      .catch(error => $log.debug('OTA sync error', error))
      .then(() => otaUpdates.cleanupOldBundles())
      .then(() => otaUpdates.purgeOrphanBundles())
      .then(() => otaUpdates.reportStorageUsage())

    const { App } = await import('@capacitor/app')

    App.addListener('appStateChange', appState => {
      otaUpdates.sync(appState.isActive)
    })
  }

  inject('otaUpdates', otaUpdates)
}
